import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, SquarePen, Trash2, MessageSquare, PanelLeft } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';
import { ChatMainContent } from '../../../components/agno/ChatMainContent';
import { ChatInterface } from '../../../components/agno/ChatInterface';
import useStore from '../../../components/agno/store';
import useChatActions from '../../../components/agno/hooks/useChatActions';
import useAIChatStreamHandler from '../../../components/agno/hooks/useAIChatStreamHandler';
import useSessionLoader from '../../../components/agno/hooks/useSessionLoader';
import { deleteTeamSessionAPI, generateSessionTitleAPI, renameSessionAPI } from '../../../components/agno/api/os';
import type { SessionEntry } from '../../../types/os';

// ─── Time helpers ───────────────────────────────────────────────────────────

type AIChatLaunchState = {
  initialMessage?: string;
  initialState?: Record<string, unknown>;
};

const toEpochMs = (ts: number | string | undefined): number => {
  if (typeof ts === 'number') {
    return ts > 1e12 ? ts : ts * 1000;
  }
  if (typeof ts === 'string') {
    const asNumber = Number(ts);
    if (!Number.isNaN(asNumber) && ts.trim() !== '') {
      return asNumber > 1e12 ? asNumber : asNumber * 1000;
    }
    const parsed = new Date(ts);
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
  }
  return 0;
};

function getTimeGroup(epochMs: number): string {
  const now = new Date();
  const date = new Date(epochMs);
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays <= 7) return 'This week';
  if (diffDays <= 30) return 'This month';
  return 'Older';
}

function formatRelativeTime(epochMs: number): string {
  const now = new Date();
  const diffMs = now.getTime() - epochMs;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d`;
  return new Date(epochMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const GROUP_ORDER = ['Today', 'This week', 'This month', 'Older'];

// ─── Component ──────────────────────────────────────────────────────────────

export const AIChatPage: React.FC = () => {
  const isConnected = useStore((s) => s.isEndpointActive);
  const isConnecting = useStore((s) => s.isEndpointLoading);
  const sessionId = useStore((s) => s.sessionId);
  const messages = useStore((s) => s.messages);
  const agents = useStore((s) => s.agents);
  const brainAgentId = useStore((s) => s.brainAgentId);
  const selectedEndpoint = useStore((s) => s.selectedEndpoint);
  const sessions = useStore((s) => s.sessionsData) || [];
  const isSessionsLoading = useStore((s) => s.isSessionsLoading);
  const setSessionId = useStore((s) => s.setSessionId);
  const isStreaming = useStore((s) => s.isStreaming);
  const setSessionsData = useStore((s) => s.setSessionsData);
  const { initialize, clearChat } = useChatActions();
  const { getSessions, getSession } = useSessionLoader();
  const { handleStreamResponse } = useAIChatStreamHandler();
  const location = useLocation();
  const initialMessageProcessed = React.useRef(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectingId, setSelectingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const brainAgent = useMemo(
    () => agents.find((agent) => agent.id === 'cs_brain_skills') || agents[0],
    [agents]
  );

  const sortedSessions = useMemo(
    () => [...sessions].sort((a, b) => toEpochMs(b.updated_at ?? b.created_at) - toEpochMs(a.updated_at ?? a.created_at)),
    [sessions]
  );

  const filteredSessions = useMemo(() => {
    if (!searchQuery.trim()) return sortedSessions;
    const q = searchQuery.toLowerCase();
    return sortedSessions.filter(
      (s) => (s.session_name || 'Conversation').toLowerCase().includes(q)
    );
  }, [sortedSessions, searchQuery]);

  const groupedSessions = useMemo(() => {
    const groups: Record<string, SessionEntry[]> = {};
    filteredSessions.forEach((session) => {
      const epochMs = toEpochMs(session.updated_at ?? session.created_at);
      const group = epochMs > 0 ? getTimeGroup(epochMs) : 'Older';
      if (!groups[group]) groups[group] = [];
      groups[group].push(session);
    });
    return groups;
  }, [filteredSessions]);

  const loadSessions = useCallback(async () => {
    if (!brainAgent?.db_id) return;
    await getSessions({
      entityType: 'agent',
      teamId: null,
      agentId: brainAgent.db_id,
      dbId: brainAgent.db_id,
    });
  }, [brainAgent?.db_id, getSessions]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Track session state before streaming starts to detect new sessions
  const prevStreamingRef = React.useRef(false);
  const sessionIdBeforeStreamRef = React.useRef<string | null>(null);

  // Capture session ID when streaming starts
  useEffect(() => {
    if (isStreaming && !prevStreamingRef.current) {
      sessionIdBeforeStreamRef.current = sessionId;
    }
  }, [isStreaming, sessionId]);

  // When streaming finishes: reload sessions + auto-generate title for new sessions
  useEffect(() => {
    if (prevStreamingRef.current && !isStreaming) {
      loadSessions();

      // If a new session was created (sessionId changed from null/different), generate AI title
      const prevSid = sessionIdBeforeStreamRef.current;
      const currentSid = useStore.getState().sessionId;
      if (currentSid && currentSid !== prevSid) {
        const currentMessages = useStore.getState().messages;
        const userMsg = currentMessages.find((m) => m.role === 'user');
        const aiMsg = currentMessages.find((m) => m.role === 'agent' && m.content);

        if (userMsg?.content) {
          // Fire-and-forget: generate title then rename
          (async () => {
            try {
              const title = await generateSessionTitleAPI(
                userMsg.content as string,
                typeof aiMsg?.content === 'string' ? aiMsg.content.slice(0, 300) : undefined
              );
              if (!title) return;

              // Rename in Agno DB so it persists
              await renameSessionAPI(
                selectedEndpoint,
                currentSid,
                title,
                'agent',
                brainAgent?.db_id
              );

              // Update local store immediately so sidebar reflects the new title
              setSessionsData((prev) =>
                (prev || []).map((s) =>
                  s.session_id === currentSid ? { ...s, session_name: title } : s
                )
              );
            } catch (e) {
              // Silent fail - the truncated title is already shown
              console.warn('Auto-title generation failed:', e);
            }
          })();
        }
      }
    }
    prevStreamingRef.current = isStreaming;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStreaming]);

  useEffect(() => {
    initialMessageProcessed.current = false;
  }, [location.key]);

  const startChatWithPayload = useCallback((payload: string | { message: string; state?: Record<string, unknown> }) => {
    if (!isConnected) return;

    if (!brainAgentId) {
      console.warn('Brain agent not ready yet, please wait...');
      return;
    }

    if (typeof payload === 'string') {
      const trimmed = payload.trim();
      if (!trimmed) return;
      handleStreamResponse(trimmed);
      return;
    }

    const trimmed = payload.message.trim();
    if (!trimmed) return;

    const formData = new FormData();
    formData.append('message', trimmed);

    if (payload.state && Object.keys(payload.state).length > 0) {
      formData.append('state', JSON.stringify(payload.state));
    }

    handleStreamResponse(formData);
  }, [brainAgentId, handleStreamResponse, isConnected]);

  // Handle starting a chat from in-page prompts/input.
  const handleStartChat = useCallback((message?: string) => {
    if (!message) return;
    startChatWithPayload(message);
  }, [startChatWithPayload]);

  useEffect(() => {
    const state = location.state as AIChatLaunchState | null;
    if (state?.initialMessage && isConnected && brainAgentId && !initialMessageProcessed.current) {
      initialMessageProcessed.current = true;
      clearChat();

      // Start new chat
      startChatWithPayload({
        message: state.initialMessage,
        state: state.initialState,
      });

      // Clear state to prevent double send on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state, isConnected, brainAgentId, clearChat, startChatWithPayload]);

  const handleNewChat = useCallback(() => {
    clearChat();
  }, [clearChat]);

  const handleSelectSession = useCallback(
    async (session: SessionEntry) => {
      if (!brainAgent?.db_id) return;
      setSelectingId(session.session_id);
      try {
        await getSession(
          {
            entityType: 'agent',
            teamId: null,
            agentId: brainAgent.db_id,
            dbId: brainAgent.db_id,
          },
          session.session_id
        );
        setSessionId(session.session_id);
      } finally {
        setSelectingId(null);
      }
    },
    [brainAgent?.db_id, getSession, setSessionId]
  );

  const handleDeleteSession = useCallback(
    async (session: SessionEntry) => {
      if (!brainAgent?.db_id) return;
      setDeletingId(session.session_id);
      try {
        await deleteTeamSessionAPI(selectedEndpoint, brainAgent.db_id, session.session_id);
        if (sessionId === session.session_id) {
          clearChat();
        }
        toast.success('Session deleted successfully');
        await loadSessions();
      } catch {
        toast.error('Failed to delete session');
      } finally {
        setDeletingId(null);
      }
    },
    [brainAgent?.db_id, clearChat, loadSessions, selectedEndpoint, sessionId]
  );

  const showWelcome = !sessionId && messages.length === 0;
  const isReady = isConnected && !!brainAgentId && agents.length > 0;

  return (
    <div className="flex h-[calc(100vh-48px)]">
      {/* Sidebar */}
      <aside
        className={cn(
          "overflow-hidden border-r border-border bg-background transition-all duration-200 flex-shrink-0",
          isHistoryOpen ? "w-72" : "w-0 border-r-0"
        )}
      >
        {isHistoryOpen && (
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsHistoryOpen(false)}
                  className="w-7 h-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  title="Close sidebar"
                >
                  <PanelLeft className="w-3.5 h-3.5" />
                </button>
                <span className="text-[13px] font-semibold text-foreground">Chats</span>
              </div>
              <button
                type="button"
                onClick={handleNewChat}
                className="w-7 h-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                title="New chat"
              >
                <SquarePen className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Search */}
            <div className="px-3 py-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search chats..."
                  className="w-full bg-muted/50 rounded-md pl-8 pr-3 py-1.5 text-[12px] text-foreground placeholder:text-muted-foreground border border-transparent focus:border-border focus:outline-none transition-colors"
                />
              </div>
            </div>

            {/* Chat list */}
            <div className="flex-1 overflow-y-auto px-2 pb-3">
              {filteredSessions.length === 0 ? (
                <div className="px-3 py-8 text-center">
                  {sessions.length === 0 ? (
                    <>
                      <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                        <MessageSquare className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <p className="text-[13px] font-medium text-foreground">No chats yet</p>
                      <p className="text-[12px] text-muted-foreground">Start a new chat to create one.</p>
                    </>
                  ) : (
                    <p className="text-[12px] text-muted-foreground">No chats found</p>
                  )}
                </div>
              ) : (
                GROUP_ORDER.map((groupName) => {
                  const items = groupedSessions[groupName];
                  if (!items || items.length === 0) return null;

                  return (
                    <div key={groupName} className="mb-1">
                      <div className="px-2 pt-3 pb-1">
                        <span className="text-[11px] font-medium text-muted-foreground">
                          {groupName}
                        </span>
                      </div>
                      {items.map((session) => {
                        const isActive = sessionId === session.session_id;
                        const epochMs = toEpochMs(session.updated_at ?? session.created_at);

                        return (
                          <div
                            key={session.session_id}
                            className={cn(
                              "w-full text-left px-2.5 py-2 rounded-md transition-colors group flex items-center gap-1",
                              isActive
                                ? "bg-muted text-foreground"
                                : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                            )}
                          >
                            <button
                              type="button"
                              onClick={() => {
                                handleSelectSession(session).catch(() => null);
                              }}
                              className="flex-1 min-w-0 text-left"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span
                                  className={cn(
                                    "text-[13px] truncate",
                                    isActive ? "font-medium" : ""
                                  )}
                                  title={session.session_name || 'Conversation'}
                                >
                                  {session.session_name || 'Conversation'}
                                </span>
                                <span className="text-[10px] text-muted-foreground flex-shrink-0">
                                  {epochMs > 0 ? formatRelativeTime(epochMs) : '—'}
                                </span>
                              </div>
                            </button>
                            {selectingId === session.session_id && (
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary flex-shrink-0" />
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                handleDeleteSession(session).catch(() => null);
                              }}
                              disabled={deletingId === session.session_id}
                              className="w-6 h-6 rounded-md flex items-center justify-center text-destructive opacity-0 group-hover:opacity-100 hover:bg-destructive/10 transition-all flex-shrink-0 disabled:opacity-100"
                              title="Delete session"
                            >
                              {deletingId === session.session_id ? (
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-destructive" />
                              ) : (
                                <Trash2 className="w-3.5 h-3.5" />
                              )}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </aside>

      {/* Main content */}
      <div className="min-w-0 min-h-0 flex-1 flex flex-col">
        {/* Minimal header - only show toggle when sidebar is closed */}
        {!isHistoryOpen && (
          <div className="flex-shrink-0 flex items-center px-3 py-2 border-b border-border">
            <button
              type="button"
              onClick={() => setIsHistoryOpen(true)}
              className="w-7 h-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              title="Open chat history"
            >
              <PanelLeft className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="min-w-0 min-h-0 flex-1">
          {showWelcome ? (
            <ChatMainContent onStartChat={handleStartChat} isReady={isReady} />
          ) : (
            <ChatInterface onStartChat={handleStartChat} isReady={isReady} />
          )}
        </div>
      </div>
    </div>
  );
};

export default AIChatPage;
